import type { DataDrivenPropertyValueSpecification, Map, ResolvedImageSpecification } from 'mapbox-gl';
import { Layer, MapStyle } from './types';

export interface IconConfig {
	id: string;
	src: string;
	width?: number;
	height?: number;
}

type IconStyleConfig = {
	style: MapStyle;
	defaultIcon: MapIconId;
	defaultSize: number;
	conditionalIcons?: IconCondition[];
	conditionalSizes?: IconSizeCondition;
};

export interface LayerIconConfig {
	styleIcons: IconStyleConfig[];
}

export enum MapIconId {
	LANDOWNER_DEFAULT = 'landowner-default',
	LANDOWNER_DEPLOYED = 'landowner-deployed',

	LANDOWNER_DEFAULT_DARK = 'landowner-default-dark',
	LANDOWNER_DEPLOYED_DARK = 'landowner-deployed-dark',

	DEVICE_DEFAULT = 'device-default',
	DEVICE_DEFAULT_DARK = 'device-default-dark',

	TTMS_DEFAULT = 'ttms-default',
	TTMS_DEFAULT_DARK = 'ttms-default-dark',

	EIA_SUBSTATIONS_DEFAULT = 'eia-substations-default',

	PLANNED_PLANT_DEFAULT = 'planned-plant-default',

	SOLAR_PLANT_DEFAULT = 'solar-plant',

	WIND_PLANT_DEFAULT = 'wind-plant',

	STORAGE_PLANT_DEFAULT = 'storage-plant',

	NUCLEAR_PLANT_DEFAULT = 'nuclear-plant',

	HYDRO_PLANT_DEFAULT = 'hydro-plant',

	BIOMASS_PLANT_DEFAULT = 'biomass-plant',

	GEOTHERMAL_PLANT_DEFAULT = 'geothermal-plant',

	GAS_PLANT_DEFAULT = 'gas-plant',

	COAL_PLANT_DEFAULT = 'coal-plant',

	LOAD_DEFAULT = 'load-default',

	DATACENTER_LOAD_DEFAULT = 'datacenter-load',

	CARBON_CAPTURE_LOAD_DEFAULT = 'carbon-load',

	MANUFACTURING_LOAD_DEFAULT = 'manufacturing-load',

	OIL_LOAD_DEFAULT = 'oil-load',

	HYDROGEN_LOAD_DEFAULT = 'hydrogen-load',

	EV_LOAD_DEFAULT = 'ev-load'
}

type IconCondition = {
	property: string;
	values: Record<string, MapIconId>;
	defaultIcon: MapIconId;
};

type IconSizeConfig = {
	size: number;
	min: number;
	max: number;
};

type IconSizeCondition = {
	property: string;
	sizes: IconSizeConfig[];
	defaultSize: number;
};

export const LAYER_ICONS: Partial<Record<Layer, LayerIconConfig>> = {
	[Layer.Landowners]: {
		styleIcons: [
			{
				style: 'light',
				defaultIcon: MapIconId.LANDOWNER_DEFAULT,
				defaultSize: 1,
				conditionalIcons: [
					{
						property: 'status',
						values: {
							DEPLOYED: MapIconId.LANDOWNER_DEPLOYED
						},
						defaultIcon: MapIconId.LANDOWNER_DEFAULT
					}
				]
			},
			{
				style: 'dark',
				defaultIcon: MapIconId.LANDOWNER_DEFAULT_DARK,
				defaultSize: 1,
				conditionalIcons: [
					{
						property: 'status',
						values: {
							DEPLOYED: MapIconId.LANDOWNER_DEPLOYED_DARK
						},
						defaultIcon: MapIconId.LANDOWNER_DEFAULT_DARK
					}
				]
			}
		]
	},
	[Layer.Devices]: {
		styleIcons: [
			{
				style: 'light',
				defaultIcon: MapIconId.DEVICE_DEFAULT,
				defaultSize: 1
			},
			{
				style: 'dark',
				defaultIcon: MapIconId.DEVICE_DEFAULT_DARK,
				defaultSize: 1
			}
		]
	},
	[Layer.Ttms]: {
		styleIcons: [
			{
				style: 'light',
				defaultIcon: MapIconId.TTMS_DEFAULT,
				defaultSize: 1
			},
			{
				style: 'dark',
				defaultIcon: MapIconId.TTMS_DEFAULT_DARK,
				defaultSize: 1
			}
		]
	},
	[Layer.EiaSubstations]: {
		styleIcons: [
			{
				style: 'light',
				defaultIcon: MapIconId.EIA_SUBSTATIONS_DEFAULT,
				defaultSize: 1
			}
		]
	},
	[Layer.PlannedPlants]: {
		styleIcons: [
			{
				style: 'light',
				defaultIcon: MapIconId.PLANNED_PLANT_DEFAULT,
				conditionalIcons: [
					{
						property: 'primaryFuel',
						values: {
							solar: MapIconId.SOLAR_PLANT_DEFAULT,
							wind: MapIconId.WIND_PLANT_DEFAULT,
							storage: MapIconId.STORAGE_PLANT_DEFAULT,
							nuclear: MapIconId.NUCLEAR_PLANT_DEFAULT,
							hydro: MapIconId.HYDRO_PLANT_DEFAULT,
							biomass: MapIconId.BIOMASS_PLANT_DEFAULT,
							geothermal: MapIconId.GEOTHERMAL_PLANT_DEFAULT,
							gas: MapIconId.GAS_PLANT_DEFAULT,
							coal: MapIconId.COAL_PLANT_DEFAULT
						},
						defaultIcon: MapIconId.PLANNED_PLANT_DEFAULT
					}
				],
				defaultSize: 1,
				conditionalSizes: {
					property: 'mwNameplate',
					sizes: [
						{ size: 0.5, min: -Infinity, max: 10 },
						{ size: 0.6, min: 10, max: 20 },
						{ size: 0.7, min: 20, max: 30 },
						{ size: 0.8, min: 30, max: 50 },
						{ size: 0.9, min: 50, max: 80 },
						{ size: 1, min: 80, max: 130 },
						{ size: 1.1, min: 130, max: 210 },
						{ size: 1.2, min: 210, max: 340 },
						{ size: 1.3, min: 340, max: 550 },
						{ size: 1.4, min: 550, max: 890 },
						{ size: 1.5, min: 890, max: Infinity }
					],
					defaultSize: 0.5
				}
			}
		]
	},
	[Layer.Loads]: {
		styleIcons: [
			{
				style: 'light',
				defaultIcon: MapIconId.LOAD_DEFAULT,
				conditionalIcons: [
					{
						property: 'facilityCategory',
						values: {
							"Data Center": MapIconId.DATACENTER_LOAD_DEFAULT,
							"Carbon Capture": MapIconId.CARBON_CAPTURE_LOAD_DEFAULT,
							"Manufacturing": MapIconId.MANUFACTURING_LOAD_DEFAULT,
							"Oil & Gas Infrastructure": MapIconId.OIL_LOAD_DEFAULT,
							"Hydrogen": MapIconId.HYDROGEN_LOAD_DEFAULT,
							"EV Fleet Charging": MapIconId.EV_LOAD_DEFAULT
						},
						defaultIcon: MapIconId.LOAD_DEFAULT
					}
				],
				defaultSize: 1,
				conditionalSizes: {
					property: 'powerCapacityMw',
					sizes: [
						{ size: 0.5, min: -Infinity, max: 10 },
						{ size: 0.6, min: 10, max: 20 },
						{ size: 0.7, min: 20, max: 30 },
						{ size: 0.8, min: 30, max: 50 },
						{ size: 0.9, min: 50, max: 80 },
						{ size: 1, min: 80, max: 130 },
						{ size: 1.1, min: 130, max: 210 },
						{ size: 1.2, min: 210, max: 340 },
						{ size: 1.3, min: 340, max: 550 },
						{ size: 1.4, min: 550, max: 890 },
						{ size: 1.5, min: 890, max: Infinity }
					],
					defaultSize: 0.5
				}
			}
		]
	}
};

const ICON_SIZE: number = 22;

export const MAP_ICONS: IconConfig[] = [
	{
		id: MapIconId.LANDOWNER_DEFAULT,
		src: '/map-icons/defaults/landowner-default.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},
	{
		id: MapIconId.LANDOWNER_DEPLOYED,
		src: '/map-icons/light/landowner-deployed.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},

	{
		id: MapIconId.LANDOWNER_DEFAULT_DARK,
		src: '/map-icons/defaults/landowner-default.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},
	{
		id: MapIconId.LANDOWNER_DEPLOYED_DARK,
		src: '/map-icons/dark/landowner-deployed.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},
	{
		id: MapIconId.DEVICE_DEFAULT,
		src: '/map-icons/defaults/device-default.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},
	{
		id: MapIconId.DEVICE_DEFAULT_DARK,
		src: '/map-icons/defaults/device-default.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},
	{
		id: MapIconId.TTMS_DEFAULT,
		src: '/map-icons/defaults/ttms-default.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},
	{
		id: MapIconId.TTMS_DEFAULT_DARK,
		src: '/map-icons/dark/ttms-default.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},
	{
		id: MapIconId.EIA_SUBSTATIONS_DEFAULT,
		src: '/map-icons/defaults/eia-substations-default.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},
	{
		id: MapIconId.PLANNED_PLANT_DEFAULT,
		src: '/map-icons/defaults/planned-plant-default.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},
	{
		id: MapIconId.SOLAR_PLANT_DEFAULT,
		src: '/map-icons/defaults/solar-plant.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},
	{
		id: MapIconId.WIND_PLANT_DEFAULT,
		src: '/map-icons/defaults/wind-plant.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},
	{
		id: MapIconId.STORAGE_PLANT_DEFAULT,
		src: '/map-icons/defaults/storage-plant.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},
	{
		id: MapIconId.NUCLEAR_PLANT_DEFAULT,
		src: '/map-icons/defaults/nuclear-plant.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},
	{
		id: MapIconId.HYDRO_PLANT_DEFAULT,
		src: '/map-icons/defaults/hydro-plant.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},
	{
		id: MapIconId.BIOMASS_PLANT_DEFAULT,
		src: '/map-icons/defaults/biomass-plant.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},
	{
		id: MapIconId.GEOTHERMAL_PLANT_DEFAULT,
		src: '/map-icons/defaults/geothermal-plant.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},
	{
		id: MapIconId.GAS_PLANT_DEFAULT,
		src: '/map-icons/defaults/gas-plant.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},
	{
		id: MapIconId.COAL_PLANT_DEFAULT,
		src: '/map-icons/defaults/coal-plant.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},
	{
		id: MapIconId.LOAD_DEFAULT,
		src: '/map-icons/defaults/load-default.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},
	{
		id: MapIconId.DATACENTER_LOAD_DEFAULT,
		src: '/map-icons/defaults/datacenter-load.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},
	{
		id: MapIconId.CARBON_CAPTURE_LOAD_DEFAULT,
		src: '/map-icons/defaults/carbon-load.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},
	{
		id: MapIconId.MANUFACTURING_LOAD_DEFAULT,
		src: '/map-icons/defaults/manufacturing-load.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},
	{
		id: MapIconId.OIL_LOAD_DEFAULT,
		src: '/map-icons/defaults/oil-load.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},
	{
		id: MapIconId.HYDROGEN_LOAD_DEFAULT,
		src: '/map-icons/defaults/hydrogen-load.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	},
	{
		id: MapIconId.EV_LOAD_DEFAULT,
		src: '/map-icons/defaults/ev-load.png',
		width: ICON_SIZE,
		height: ICON_SIZE
	}
];

export async function loadMapIcons(map: Map): Promise<void> {
	if (!map.isStyleLoaded()) {
		await new Promise<void>(resolve => {
			map.once('style.load', () => resolve());
		});
	}

	await Promise.all(MAP_ICONS.map(icon => loadMapIcon(map, icon)));
}

async function loadMapIcon(map: Map, icon: IconConfig): Promise<void> {
	return new Promise((resolve, reject) => {
		if (map.hasImage(icon.id)) {
			resolve();
			return;
		}

		const img = new Image(icon.width || 24, icon.height || 24);

		img.onload = () => {
			try {
				map.addImage(icon.id, img);
				resolve();
			} catch (err) {
				reject(err);
			}
		};

		img.onerror = err => {
			console.error(err);
			reject(new Error(`Failed to load icon: ${icon.src}`));
		};

		img.crossOrigin = 'anonymous';

		img.src = icon.src;
	});
}

export function getIconExpression(
	layer: Layer,
	mapStyle: string
): DataDrivenPropertyValueSpecification<ResolvedImageSpecification> {
	const config = LAYER_ICONS[layer];

	if (!config) {
		return ['literal', 'default'];
	}

	const styleConfig = config.styleIcons.find(s => s.style === mapStyle);

	if (!styleConfig) {
		const fallbackConfig = config.styleIcons[0];
		return getExpressionFromStyleConfig(fallbackConfig);
	}

	return getExpressionFromStyleConfig(styleConfig);
}

function getExpressionFromStyleConfig(
	styleConfig?: IconStyleConfig | null | undefined
): DataDrivenPropertyValueSpecification<ResolvedImageSpecification> {
	if (!styleConfig) {
		return ['literal', 'default'];
	}

	if (!styleConfig.conditionalIcons || styleConfig.conditionalIcons.length === 0) {
		return ['literal', styleConfig.defaultIcon];
	}

	const expressions = styleConfig.conditionalIcons.map(styleConditionToCaseExpression);

	if (expressions.length === 1) {
		return expressions[0];
	}

	return expressions.reduce((prev, curr) => {
		return [
			'case',
			['!=', prev, styleConfig.defaultIcon],
			prev,
			curr
		] as DataDrivenPropertyValueSpecification<ResolvedImageSpecification>;
	});
}

function styleConditionToCaseExpression(
	condition: IconCondition
): DataDrivenPropertyValueSpecification<ResolvedImageSpecification> {
	const caseExpression: DataDrivenPropertyValueSpecification<ResolvedImageSpecification> = ['case'];

	Object.entries(condition.values).forEach(([value, iconId]) => {
		caseExpression.push(
			[
				'any',
				['==', ['get', condition.property], value],
				['in', ['downcase', value], ['downcase', ['get', condition.property]]]
			],
			iconId
		);
	});

	caseExpression.push(condition.defaultIcon);
	return caseExpression;
}

export function getIconSizeExpression(layer: Layer, mapStyle: string): DataDrivenPropertyValueSpecification<number> {
	const config = LAYER_ICONS[layer];

	if (!config) {
		return ['literal', 'default'];
	}

	const styleConfig = config.styleIcons.find(s => s.style === mapStyle);

	if (!styleConfig) {
		const fallbackConfig = config.styleIcons[0];
		return getSizeExpressionFromStyleConfig(fallbackConfig);
	}

	return getSizeExpressionFromStyleConfig(styleConfig);
}

function getSizeExpressionFromStyleConfig(
	styleConfig?: IconStyleConfig | null | undefined
): DataDrivenPropertyValueSpecification<number> {
	if (!styleConfig) {
		return ['literal', 'default'];
	}

	if (!styleConfig.conditionalSizes) {
		return ['literal', styleConfig.defaultSize];
	}

	const { property, sizes, defaultSize } = styleConfig.conditionalSizes;

	const stepExpression: DataDrivenPropertyValueSpecification<number> = [
		'step',
		['to-number', ['get', property]],
		defaultSize
	];

	const sortedSizes = [...sizes].sort((a, b) => a.min - b.min);

	sortedSizes.forEach(config => {
		if (config.min !== -Infinity) {
			stepExpression.push(config.min, config.size);
		}
	});

	return stepExpression;
}
